import { useQuery } from '@tanstack/react-query';

import { isRetryablePortalError } from '../api/portalRequestError';
import { fetchSetupPortalState } from '../api/setupPortalApi';
import { onboardQueryKeys } from '../onboardQueryKeys';

import type { UseQueryResult } from '@tanstack/react-query';

import type { ISetupPortalState } from '../api/setupPortalApi';

const MAX_RETRIES = 2;

interface UseSetupPortalStateOptions {
  token: string;
  expired?: boolean;
}

// Read-only setup-portal state for the token-gated recipient SPA. `expired`
// (an already-known, server-provided flag) disables the query entirely so an
// expired token never triggers a network call. On a live token, a 4xx/410
// response means the token is invalid/revoked and retrying cannot help, so
// only transient failures (network errors, 5xx) get a couple of retries.
export function useSetupPortalState({
  token,
  expired = false
}: UseSetupPortalStateOptions): UseQueryResult<ISetupPortalState, Error> {
  return useQuery({
    queryKey: onboardQueryKeys.setupPortalState(token),
    queryFn: async () => fetchSetupPortalState(token),
    enabled: !expired && !!token,
    retry: (failureCount, error) => isRetryablePortalError(error) && failureCount < MAX_RETRIES
  });
}
